/**
 * Decor de fond : sphere et anneau de verre, rendus 3D.
 *
 * Ce sont des rendus GENERES (Z-Image), et c'est assume : une sphere de verre
 * n'a aucune identite a usurper, contrairement a un visage ou une main. Le
 * fond noir du rendu est transforme en alpha par la luminance, ce qui conserve
 * le halo au lieu de le trancher net.
 *
 * Retenue volontaire : la derive est lente, l'amplitude faible, et tout se
 * fige sous prefers-reduced-motion. Un decor qui bouge vite est le tell d'un
 * site genere ; un decor qui respire est le contraire.
 */

import type { CSSProperties } from "react";

type Forme = "sphere" | "anneau";

interface Props {
  forme: Forme;
  /** Taille en pourcentage de la largeur du conteneur. */
  taille: string;
  /** Positionnement libre. */
  style: CSSProperties;
  /** Opacite du decor. Le fond doit rester un fond. */
  opacite?: number;
  /** Duree d'un aller-retour, en secondes. */
  duree?: number;
  /** Decalage pour desynchroniser deux formes. */
  retard?: number;
  flou?: number;
}

export function GlassDecor({
  forme,
  taille,
  style,
  opacite = 0.4,
  duree = 34,
  retard = 0,
  flou = 0,
}: Props) {
  return (
    <picture>
      <source media="(max-width: 767px)" srcSet={`/decor/${forme}-128.webp`} />
      <img
        src={`/decor/${forme}-320.webp`}
        srcSet={`/decor/${forme}-192.webp 192w, /decor/${forme}-320.webp 320w, /decor/${forme}-520.webp 520w, /decor/${forme}-760.webp 760w`}
        sizes={taille}
        alt=""
        aria-hidden="true"
        width={760}
        height={760}
        loading="lazy"
        decoding="async"
        className="decor-derive pointer-events-none absolute select-none"
        style={{
          width: taille,
          height: "auto",
          opacity: opacite,
          filter: flou ? `blur(${flou}px)` : undefined,
          animationDuration: `${duree}s`,
          animationDelay: `${retard}s`,
          ...style,
        }}
      />
    </picture>
  );
}

/** Les keyframes, montees une seule fois par page. */
export const DecorStyles = () => (
  <style dangerouslySetInnerHTML={{ __html: `
    /* Pas de scale : l'element porte un flou, et changer son echelle force
       une re-tramage complet a chaque image. La rotation, elle, ne change pas
       l'echelle de tramage et reste composee. */
    @keyframes decorDerive {
      0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
      50%      { transform: translate3d(2.5%, -4%, 0) rotate(6deg); }
    }
    .decor-derive { animation-name: decorDerive; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
    @media (prefers-reduced-motion: reduce) { .decor-derive { animation: none; } }
  ` }} />
);
